import { useMemo } from 'react'

/**
 * A deliberately small Markdown renderer.
 *
 * We could npm-install a real one. We don't, for two reasons:
 *
 *  1. Most Markdown libraries render to an HTML string, which invites
 *     `dangerouslySetInnerHTML`, which is how XSS gets into React apps. This
 *     renderer emits React ELEMENTS — text is text, never markup, so a lesson
 *     containing `<script>` displays those characters instead of running them.
 *     There is no sanitiser to misconfigure because there is no HTML parsing.
 *
 *  2. Our content is written by us and needs six constructs. A 90-line file we
 *     understand beats a dependency we don't.
 *
 * If lessons ever accept user-submitted Markdown, revisit this with a real
 * parser AND a sanitiser — the current safety comes from the narrow grammar.
 */
export default function Markdown({ source }: { source: string }) {
  const blocks = useMemo(() => parse(source), [source])
  return <div className="prose">{blocks}</div>
}

function parse(src: string): JSX.Element[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n')
  const out: JSX.Element[] = []
  let i = 0
  let key = 0

  while (i < lines.length) {
    const line = lines[i]

    if (line.trim() === '') {
      i++
      continue
    }

    // Fenced code block
    if (line.startsWith('```')) {
      const lang = line.slice(3).trim()
      const body: string[] = []
      i++
      while (i < lines.length && !lines[i].startsWith('```')) {
        body.push(lines[i])
        i++
      }
      i++
      out.push(
        <pre key={key++} className="code-block" data-lang={lang || undefined}>
          <code>{body.join('\n')}</code>
        </pre>,
      )
      continue
    }

    // Heading
    const heading = /^(#{1,4})\s+(.*)$/.exec(line)
    if (heading) {
      const level = heading[1].length
      const Tag = (['h2', 'h3', 'h4', 'h5'] as const)[level - 1]
      out.push(<Tag key={key++}>{inline(heading[2])}</Tag>)
      i++
      continue
    }

    // Blockquote
    if (line.startsWith('> ')) {
      const body: string[] = []
      while (i < lines.length && lines[i].startsWith('> ')) {
        body.push(lines[i].slice(2))
        i++
      }
      out.push(<blockquote key={key++}>{inline(body.join(' '))}</blockquote>)
      continue
    }

    // Lists
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''))
        i++
      }
      out.push(
        <ul key={key++}>
          {items.map((t, n) => (
            <li key={n}>{inline(t)}</li>
          ))}
        </ul>,
      )
      continue
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = []
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''))
        i++
      }
      out.push(
        <ol key={key++}>
          {items.map((t, n) => (
            <li key={n}>{inline(t)}</li>
          ))}
        </ol>,
      )
      continue
    }

    // Paragraph
    const para: string[] = []
    while (i < lines.length && lines[i].trim() !== '' && !/^(#{1,4}\s|```|>\s|\s*[-*]\s|\s*\d+\.\s)/.test(lines[i])) {
      para.push(lines[i])
      i++
    }
    out.push(<p key={key++}>{inline(para.join(' '))}</p>)
  }

  return out
}

/** Inline: `code`, **bold**, _italic_. Everything else stays literal text. */
function inline(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = []
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*|_[^_]+_)/g
  let last = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    const token = match[0]

    if (token.startsWith('`')) {
      parts.push(<code key={key++}>{token.slice(1, -1)}</code>)
    } else if (token.startsWith('**')) {
      parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>)
    } else {
      parts.push(<em key={key++}>{token.slice(1, -1)}</em>)
    }
    last = match.index + token.length
  }

  if (last < text.length) parts.push(text.slice(last))
  return parts
}
